import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCheckout } from '../../../components/Checkout/CheckoutContext'
import { useKeys } from '../../../services/KeysProvider'
import {
  createDiscountEntry,
  createFreeItemCreditEntry,
  createGiftCardEntry,
} from '../promoItems.utils'
import type { PromoEntry } from '../promoEntries.types'
import { usePricing } from '../../../components/Pricing/PricingProvider'
import { useCheckoutPromoInputs } from './useCheckoutPromoInputs'
import { useDiscountInfo } from './useDiscountInfo'
import { useGroupCheckoutPricing } from './useGroupCheckoutPricing'

export function usePromoItems(): PromoEntry[] {
  const { t } = useTranslation()
  const { pricingSummary } = useCheckout()
  const { ipCountry } = useKeys()
  const { tileCredits, discountCoupon } = useCheckoutPromoInputs()
  const discountInfo = useDiscountInfo()
  const { discounts, pricingData } = usePricing()
  const groupPricing = useGroupCheckoutPricing()

  // The discount value is resolved by a serial pipeline: the discounts fetch
  // (PricingProvider) gates the group-pricing query. While discounts reloads,
  // the group query is disabled and reports isLoading as false, so
  // neither query alone reflects "still resolving" — checking only the group
  // query made the discount row vanish until discounts finished. Treat the
  // whole pipeline as loading instead.
  const isPricingLoading =
    discounts.isLoading || pricingData.isLoading || groupPricing.isLoading

  return useMemo(() => {
    const giftCards = pricingSummary.giftsBalances ?? []
    const items: PromoEntry[] = []

    for (const coupon of tileCredits) {
      items.push(createFreeItemCreditEntry(coupon, t, ipCountry))
    }
    for (const balance of giftCards) {
      items.push(createGiftCardEntry(balance, pricingSummary.currency, t))
    }
    // Keep the discount entry in the list while pricing is loading so its row
    // holds its place (rendered as a loading skeleton) instead of popping in
    // once pricing resolves.
    if (discountCoupon || discountInfo || isPricingLoading) {
      items.push(
        createDiscountEntry(discountCoupon, discountInfo, isPricingLoading)
      )
    }

    return items
  }, [
    tileCredits,
    discountCoupon,
    discountInfo,
    isPricingLoading,
    pricingSummary,
    t,
    ipCountry,
  ])
}
